/**
 * Feature flags — **everyone reads, the server decides who flips.**
 *
 * A flag nobody but the founder can see is a flag nobody can debug, so the list is visible to every
 * scope. The flip is not: each row carries its own `editable_by`, and the screen shows that scope
 * rather than guessing at it. The client never enforces the rule — a flip goes to the server and
 * the server's refusal is what the operator reads, verbatim.
 *
 * **`evaluations_30d` is `null`, not `0`.** The analytics pipeline that would count evaluations is
 * not built, and rendering an unknown as zero would say "nothing uses this flag" — which is the
 * sentence that gets a live capability deleted. The screen says it does not know.
 *
 * **The rollout percent is shown only for a percentage flag.** On an `on` or `off` flag the column
 * is always 0, and a 0% beside a flag that is fully live reads as a rollout that reached nobody.
 */
import { useState } from 'react';
import { InternalTesters } from './SupportDesk';
import { GatewayApprovals } from './GatewayApprovals';
import { Icon, StatusChip } from '../components/primitives';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { Input, Select } from '../components/forms';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useCreateFlag,
  useFlagReport,
  useFlagRevisions,
  useFlags,
  useFlipFlag,
  useUpdateFlag,
  type FlagRow,
} from '../lib/api/hooks/flags';
import { formatDateTime } from '../lib/datetime';

/** `on` is green, a live rollout is amber-ish, `off` and `internal` are quiet. */
/**
 * The server's key rule (`flags.KEY_PATTERN`), said in the form rather than after a round-trip:
 * bare snake_case, optionally one `city_launch:{slug}`, at most 64 characters. The server stays
 * the judge — this only stops "QA Flag!" being offered a Declare button.
 */
const FLAG_KEY = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*(?::[a-z0-9]+(?:-[a-z0-9]+)*)?$/;

export function flagKeyError(key: string): string | undefined {
  if (key === '') return undefined;
  if (key.length > 64) return 'At most 64 characters';
  if (!FLAG_KEY.test(key)) return 'snake_case, like instant_book or city_launch:patna';
  if (key.includes(':') && !key.startsWith('city_launch:')) return 'The only namespace is city_launch:';
  return undefined;
}

/** Today in IST as YYYY-MM-DD — the calendar a review date is read in. */
export function istToday(now: number = Date.now()): string {
  return new Date(now + 330 * 60_000).toISOString().slice(0, 10);
}

/** A review date is a real calendar day after today: one already past is overdue on birth. */
export function reviewDateError(value: string, today: string): string | undefined {
  if (value === '') return undefined;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
    return 'A date as YYYY-MM-DD';
  }
  return value > today ? undefined : 'A date after today';
}

export function flagTone(status: FlagRow['status']): 'success' | 'warn' | 'info' | 'neutral' {
  if (status === 'on') return 'success';
  if (status === 'percentage') return 'warn';
  if (status === 'internal') return 'info';
  return 'neutral';
}

/** What the rollout column says. See the module note — 0% on an `on` flag would be a lie. */
export function rolloutLabel(flag: FlagRow): string {
  if (flag.status === 'on') return '100%';
  if (flag.status === 'percentage') return `${flag.rollout_percent}%`;
  return '—';
}

/** `null` is not zero, and the difference is the whole point. */
export function evaluationsLabel(value: number | null | undefined): string {
  return value === null || value === undefined ? 'Not counted yet' : value.toLocaleString('en-IN');
}

/** Every scope, so a flag can be handed to the team that owns the capability. */
const SCOPE_OPTIONS = [
  'superadmin',
  'ops_dispatch',
  'supply_kyc',
  'finance',
  'catalogue_content',
  'support',
  'growth',
] as const;

const EMPTY_DRAFT = {
  flag_key: '',
  description: '',
  owner: '',
  review_on: '',
  /* Defaults to the narrowest scope. Anything else would hand a new flag's authority to
     whoever the default named, silently. */
  editable_by: 'superadmin' as FlagRow['editable_by'],
};


export function Flags() {
  const { scope, toast } = useConsole();
  const [confirm, setConfirm] = useState(false);
  const [marking, setMarking] = useState<'gateway' | 'store' | null>(null);
  const list = useFlags();
  /* The queue keys rows by `id`; a flag's identity is its key, so say that once here rather
     than threading `flag_key` through every comparison the queue makes. */
  const rows: readonly (FlagRow & { id: string })[] = (list.data?.items ?? []).map((f) => ({
    ...f,
    id: f.flag_key,
  }));
  const { focus, setFocus, open, setOpen } = useQueue(rows);
  const revisions = useFlagRevisions(open?.flag_key ?? null);
  const report = useFlagReport();
  const flip = useFlipFlag();
  const update = useUpdateFlag();
  const create = useCreateFlag();
  const [extendTo, setExtendTo] = useState('');
  const [extendWhy, setExtendWhy] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  const turningOn = open ? open.status === 'off' : false;

  const flipDialog = open && (
    <ConfirmAction
      open={confirm}
      title={`Flip ${open.flag_key}?`}
      phrase="FLIP"
      tone="error"
      reason
      consequences={[
        'Changes behaviour for every client within one config fetch of the flip.',
        `Only ${open.editable_by} may flip this one — the server checks, not this screen.`,
        'Flags are not a settings substitute — a permanent value belongs in the settings registry.',
      ]}
      onCancel={() => setConfirm(false)}
      onConfirm={(reason: string) => {
        setConfirm(false);
        flip.mutate(
          { flagKey: open.flag_key, status: turningOn ? 'on' : 'off', reason },
          {
            onSuccess: () => {
              setOpen(null);
              toast({ tone: 'success', message: `${open.flag_key} is now ${turningOn ? 'on' : 'off'}` });
            },
            /* The server's words, not ours. It is the only side that knows why. */
            onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
          },
        );
      }}
      confirmLabel="Flip flag"
    />
  );

  const gated = open?.requires_gateway_approval === true;
  /* 11-compliance/12 §10: a policy review per store, filed below as approvals from app_store and google_play. */
  const storeGated = open?.requires_store_policy_review === true;
  const lifting = marking === 'store' ? storeGated : gated;
  const markDialog = open && marking && (
    <ConfirmAction
      open
      title={
        marking === 'store'
          ? lifting
            ? `Lift the store-review mark on ${open.flag_key}?`
            : `Mark ${open.flag_key} as needing a store policy review?`
          : lifting
            ? `Lift the gateway mark on ${open.flag_key}?`
            : `Mark ${open.flag_key} as needing gateway approval?`
      }
      phrase="MARK"
      tone="warn"
      reason
      consequences={
        marking === 'store'
          ? lifting
            ? ['Its owner can then turn it on without the store reviews on file. Do this only if it no longer ships anything store review cares about.']
            : ['It cannot be turned on until a policy review from app_store and from google_play naming it is filed below.', 'Turning it off is never blocked.']
          : lifting
            ? ['Its owner can then turn it on without an approval on file. Do this only if it no longer changes what money is collected for.']
            : ['It cannot be turned on until a gateway approval naming it is filed below.', 'Turning it off is never blocked.']
      }
      onCancel={() => setMarking(null)}
      onConfirm={(reason: string) => {
        const kind = marking;
        setMarking(null);
        update.mutate(
          {
            flagKey: open.flag_key,
            ...(kind === 'store' ? { requires_store_policy_review: !lifting } : { requires_gateway_approval: !lifting }),
            clear_audience: false,
            reason,
          },
          {
            onSuccess: () => {
              setOpen(null);
              toast({
                tone: 'success',
                message: lifting ? `${open.flag_key} no longer needs ${kind === 'store' ? 'store reviews' : 'an approval'}` : `${open.flag_key} now needs ${kind === 'store' ? 'store reviews' : 'an approval'} on file`,
              });
            },
            onError: fail,
          },
        );
      }}
      confirmLabel={lifting ? 'Lift the mark' : 'Mark flag'}
    />
  );

  if (open) {
    return (
      <DetailPage
        title={open.flag_key}
        code={open.editable_by}
        status={<StatusChip tone={flagTone(open.status)} label={open.status} />}
        onClose={() => setOpen(null)}
        audit={
          open.last_flip_reason
            ? `Last flipped by admin ${open.last_flip_by_admin_id} — “${open.last_flip_reason}”`
            : 'Never flipped since it was declared.'
        }
        actions={
          <>
            <ScopeAction scope={scope} need="superadmin" size="sm" variant="ghost" onClick={() => setMarking('gateway')}>
              {gated ? 'Lift gateway mark' : 'Needs gateway approval'}
            </ScopeAction>
            <ScopeAction scope={scope} need="superadmin" size="sm" variant="ghost" onClick={() => setMarking('store')}>
              {storeGated ? 'Lift store-review mark' : 'Needs store review'}
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need={open.editable_by}
              size="sm"
              variant={turningOn ? 'primary' : 'destructive'}
              onClick={() => setConfirm(true)}
            >
              {turningOn ? 'Turn on' : 'Turn off'}
            </ScopeAction>
          </>
        }
      >
        <KV
          rows={[
            ['Gates', open.description],
            ['Owner', open.owner],
            ['May flip it', open.editable_by],
            ['Rollout', rolloutLabel(open)],
            ['Audience', open.audience ? JSON.stringify(open.audience) : 'Everybody'],
            ['Review due', `${open.review_on}${open.overdue ? ' · overdue' : ''}`],
            ['Evaluations, 30 d', evaluationsLabel(open.evaluations_30d)],
            [
              'Gateway approval',
              gated ? 'Required — stays off until an approval naming it is on file' : 'Not required',
            ],
            [
              'Store policy review',
              storeGated ? 'Required — stays off until app_store and google_play reviews naming it are on file' : 'Not required',
            ],
          ]}
        />

        <Section title="Flip history" note="Whole rows on each side, newest first">
          {revisions.isPending && <LoadingState message="Reading the flip history…" />}
          {revisions.isError && (
            <ErrorState
              title="Could not read the flip history"
              cause={errorMessage(revisions.error)}
            />
          )}
          {revisions.isSuccess && revisions.data.items.length === 0 && (
            <EmptyState title="No changes yet" body="This flag has not moved since it was declared." />
          )}
          {revisions.isSuccess && revisions.data.items.length > 0 && (
            <DataTable
              columns={[
                { key: 'when', label: 'When' },
                { key: 'who', label: 'Admin', mono: true },
                { key: 'reason', label: 'Reason' },
              ]}
              rows={revisions.data.items.map((r) => ({
                id: String(r.id),
                when: formatDateTime(r.created_at),
                who: `admin #${r.admin_id}`,
                reason: r.reason,
              }))}
            />
          )}
        </Section>
        <Section
          title="Extend the review"
          note="Graduate, extend or kill — extending is the one that needs a new date and a reason"
        >
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end' }}>
            <Input
              label="New review date"
              value={extendTo}
              onChange={setExtendTo}
              placeholder="2027-03-01"
              helper="ISO date, as the API stores it"
              error={reviewDateError(extendTo, istToday())}
            />
            <Input label="Why it stays" value={extendWhy} onChange={setExtendWhy} style={{ flex: 1 }} />
            <ScopeAction
              scope={scope}
              need={open.editable_by}
              size="sm"
              disabled={
                extendTo === '' || reviewDateError(extendTo, istToday()) !== undefined || extendWhy.trim() === ''
              }
              onClick={() =>
                update.mutate(
                  {
                    flagKey: open.flag_key,
                    review_on: extendTo,
                    /* Explicit: an extension must not wipe the targeting rules, and the
                       server treats an omitted audience as "leave it" only because this
                       flag says so. */
                    clear_audience: false,
                    reason: extendWhy.trim(),
                  },
                  {
                    onSuccess: () => {
                      setExtendTo('');
                      setExtendWhy('');
                      setOpen(null);
                      toast({ tone: 'success', message: `Review moved to ${extendTo}` });
                    },
                    onError: fail,
                  },
                )
              }
            >
              Extend
            </ScopeAction>
          </div>
        </Section>
        {flipDialog}
        {markDialog}
      </DetailPage>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-warn-bg)',
          color: 'var(--c-warn-text)',
          border: '1px solid var(--c-warn)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
        }}
      >
        <Icon name="toggle-left" size={16} />
        <span style={{ flex: 1 }}>
          Every admin can read flags; each one names the scope that may flip it. A flag past its
          review date is one nobody has decided to keep.
        </span>
      </div>

      {/* The governance view, above the inventory: a flag past its review date is one nobody
          has decided to keep, and a percentage flag parked past review is worse — half the
          users are on one code path and half on another, indefinitely. */}
      {report.isSuccess && report.data.overdue.length > 0 && (
        <Section
          title={`${report.data.overdue.length} past review`}
          note={
            report.data.parked.length > 0
              ? `${report.data.parked.length} of them are percentage rollouts left parked part-way`
              : 'Each needs its owner to graduate, extend or kill it'
          }
        >
          <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)' }}>
            {report.data.overdue.map((f) => f.flag_key).join(' · ')}
          </p>
        </Section>
      )}

      <Section title="Declare a flag" note="Born off, always owned, always with a review date">
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
          <Input
            label="Key"
            value={draft.flag_key}
            onChange={(v) => setDraft({ ...draft, flag_key: v })}
            placeholder="instant_book"
            error={flagKeyError(draft.flag_key)}
          />
          <Input
            label="What it gates"
            value={draft.description}
            onChange={(v) => setDraft({ ...draft, description: v })}
          />
          <Input
            label="Owner"
            value={draft.owner}
            onChange={(v) => setDraft({ ...draft, owner: v })}
            placeholder="Growth"
          />
          <Input
            label="Review on"
            value={draft.review_on}
            onChange={(v) => setDraft({ ...draft, review_on: v })}
            placeholder="2027-03-01"
            error={reviewDateError(draft.review_on, istToday())}
          />
          <Select
            label="May flip it"
            value={draft.editable_by}
            onChange={(v) => setDraft({ ...draft, editable_by: v as FlagRow['editable_by'] })}
            options={SCOPE_OPTIONS}
          />
          <ScopeAction
            scope={scope}
            need="superadmin"
            size="sm"
            disabled={
              Object.values(draft).some((v) => v === '') ||
              flagKeyError(draft.flag_key) !== undefined ||
              reviewDateError(draft.review_on, istToday()) !== undefined
            }
            onClick={() =>
              create.mutate(
                { ...draft, reason: `Declared ${draft.flag_key}` },
                {
                  onSuccess: () => {
                    setDraft(EMPTY_DRAFT);
                    toast({ tone: 'success', message: `${draft.flag_key} declared, off` });
                  },
                  onError: fail,
                },
              )
            }
          >
            Declare
          </ScopeAction>
        </div>
      </Section>

      {list.isPending && <LoadingState message="Reading the flag inventory…" />}
      {list.isError && (
        <ErrorState title="Could not read the flags" cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && rows.length === 0 && (
        <EmptyState
          title="No flags declared"
          body="Every capability is unconditional until somebody declares a switch for it."
        />
      )}

      {list.isSuccess && rows.length > 0 && (
        <DataTable
          density="comfortable"
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(row) => setOpen(rows.find((f) => f.flag_key === row.id)!)}
          footer={
            <>
              <span>
                {rows.length} flags · {rows.filter((f) => f.status !== 'off').length} live ·{' '}
                {rows.filter((f) => f.overdue).length} past review
              </span>
              <span>{QUEUE_HINT}</span>
            </>
          }
          columns={[
            { key: 'key', label: 'Flag', mono: true },
            { key: 'gates', label: 'What it gates' },
            { key: 'rollout', label: 'Rollout', align: 'right', mono: true },
            { key: 'owner', label: 'Owner' },
            { key: 'editor', label: 'May flip it', mono: true },
            { key: 'review', label: 'Review due' },
            { key: 'status', label: 'State' },
          ]}
          rows={rows.map((f) => ({
            id: f.flag_key,
            key: f.flag_key,
            gates: [
              f.description,
              f.requires_gateway_approval ? 'needs gateway approval' : null,
              f.requires_store_policy_review ? 'needs store review' : null,
            ]
              .filter(Boolean)
              .join(' · '),
            rollout: rolloutLabel(f),
            owner: f.owner,
            editor: f.editable_by,
            review: f.overdue ? `${f.review_on} · overdue` : f.review_on,
            status: <StatusChip tone={flagTone(f.status)} label={f.status} />,
          }))}
        />
      )}

      <InternalTesters />
      <GatewayApprovals />
    </>
  );
}
